import { normalizeCaseCode, readJson } from "./case-scoped-storage";

const DraftStorage = {
  key(caseCode) {
    return `isx_lead_draft_${normalizeCaseCode(caseCode) || "unknown"}`;
  },
  legacyKey(caseCode) {
    if (caseCode === undefined || caseCode === null) {
      return "isx_lead_draft_unknown";
    }

    return `isx_lead_draft_${caseCode.toString().trim()}`;
  },
  load(caseCode) {
    const draft = readJson(this.key(caseCode), null);
    if (draft) return draft;

    const legacyKey = this.legacyKey(caseCode);
    if (legacyKey === this.key(caseCode)) {
      return null;
    }

    return readJson(legacyKey, null);
  },
  save(caseCode, data) {
    const payload = {
      caseCode: normalizeCaseCode(caseCode),
      data,
      savedAt: new Date().toISOString(),
    };
    localStorage.setItem(this.key(caseCode), JSON.stringify(payload));
    window.dispatchEvent(
      new CustomEvent("lead-draft:updated", { detail: payload })
    );
    return payload;
  },
  clear(caseCode) {
    localStorage.removeItem(this.key(caseCode));
    window.dispatchEvent(new CustomEvent("lead-draft:updated", { detail: null }));
  },
};

const LeadDraftLocalHook = {
  mounted() {
    this.caseCode = this.el.dataset.draftCaseCode;
    this.banner = this.el.querySelector("[data-lead-draft-banner]");
    this.bannerStatus = this.el.querySelector("[data-lead-draft-status]");
    this.resumeBtn = this.el.querySelector("[data-lead-draft-resume]");
    this.discardBtn = this.el.querySelector("[data-lead-draft-discard]");
    this.fields = Array.from(this.el.querySelectorAll("input[name^='lead['], textarea[name^='lead[']"));
    this.dirty = false;
    this.submitted = false;

    const draft = DraftStorage.load(this.caseCode);
    const shouldResume = new URLSearchParams(window.location.search).get("resume_draft");

    if (draft && shouldResume) {
      this.applyDraft(draft.data);
    }

    this.updateBanner(draft);

    this.handleInput = () => {
      this.dirty = true;
      const data = this.collectData();
      DraftStorage.save(this.caseCode, data);
      this.updateBanner(DraftStorage.load(this.caseCode));
    };

    this.el.addEventListener("input", this.handleInput);

    this.handleSubmit = () => {
      this.submitted = true;
      DraftStorage.clear(this.caseCode);
      this.updateBanner(null);
    };

    this.el.addEventListener("submit", this.handleSubmit);

    this.beforeUnload = (event) => {
      if (!this.shouldWarn()) return;
      event.preventDefault();
      event.returnValue = "";
    };

    window.addEventListener("beforeunload", this.beforeUnload);

    this.resumeBtn?.addEventListener("click", () => {
      const currentDraft = DraftStorage.load(this.caseCode);
      if (currentDraft) {
        this.applyDraft(currentDraft.data);
      }
    });

    this.discardBtn?.addEventListener("click", () => {
      DraftStorage.clear(this.caseCode);
      this.updateBanner(null);
    });
  },
  destroyed() {
    this.el.removeEventListener("input", this.handleInput);
    this.el.removeEventListener("submit", this.handleSubmit);
    window.removeEventListener("beforeunload", this.beforeUnload);
  },
  collectData() {
    return this.fields.reduce((acc, field) => {
      acc[field.name] = field.value;
      return acc;
    }, {});
  },
  applyDraft(data) {
    if (!data) return;
    this.fields.forEach((field) => {
      if (Object.prototype.hasOwnProperty.call(data, field.name)) {
        field.value = data[field.name];
        field.dispatchEvent(new Event("input", { bubbles: true }));
      }
    });
  },
  updateBanner(draft) {
    if (!this.banner) return;
    if (draft && draft.savedAt) {
      const savedAt = new Date(draft.savedAt).toLocaleTimeString();
      if (this.bannerStatus) {
        this.bannerStatus.textContent = `Draft saved at ${savedAt}`;
      }
      this.banner.classList.remove("hidden");
    } else {
      this.banner.classList.add("hidden");
    }
  },
  shouldWarn() {
    if (this.submitted) return false;
    const draft = DraftStorage.load(this.caseCode);
    return this.dirty && draft && draft.savedAt;
  },
};

export default LeadDraftLocalHook;
